import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Form, Formik, useFormikContext } from "formik";
import isEqual from "lodash/isEqual";

import {
  CUSTOM_FIELD_TEMPLATES_QUERY,
  FETCH_PROJECT_QUERY,
} from "../../../constants/graphql/queries";
import { useCustomFields } from "../../../contexts/customFields";
import useUpdateProjectSettings from "../../../hooks/useUpdateProjectSettings";
import useNotification from "../../../hooks/notification";
import { isFieldSettingEnabled } from "../../../utils/fieldSettings";

function ConfigurationFormBridge({ formikRef, initialFormValues, savedInventorySettings }) {
  const formik = useFormikContext();
  const hasUnsavedInventoryChanges =
    !isEqual(formik.values.fieldSettings, savedInventorySettings.fieldSettings) ||
    !isEqual(formik.values.receivingSettings, savedInventorySettings.receivingSettings);
  const hasUnsavedChanges =
    hasUnsavedInventoryChanges ||
    !isEqual(formik.values.shipmentPdfFields, initialFormValues.shipmentPdfFields) ||
    !isEqual(formik.values.shipmentItemPdfFields, initialFormValues.shipmentItemPdfFields) ||
    !isEqual(formik.values.inventoryRequestPdf, initialFormValues.inventoryRequestPdf) ||
    !isEqual(
      formik.values.shipmentItemMoveLabelPdfFields,
      initialFormValues.shipmentItemMoveLabelPdfFields
    ) ||
    !isEqual(formik.values.storeCardFields, initialFormValues.storeCardFields);

  if (formikRef) {
    formikRef.current = {
      ...formikRef.current,
      ...formik,
      hasUnsavedChanges,
      hasUnsavedInventoryChanges,
    };
  }

  return null;
}

export default function ConfigurationForm({ children, project, onClose, formikRef }) {
  const { projectId } = useParams();
  const [updateProjectSettings] = useUpdateProjectSettings();
  const [fieldSettings, fieldsLoading] = useCustomFields(undefined, "fieldSettings");
  const [receivingSettings, receivingLoading] = useCustomFields(undefined, "receivingSettings");
  const loading = fieldsLoading || receivingLoading;
  const { notifyError, notifySuccess } = useNotification();
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [savedInventorySettings, setSavedInventorySettings] = useState(null);

  const cleanPdfFields = useCallback((orderedFields, fieldSettings) => {
    if (!orderedFields || !Array.isArray(orderedFields)) return [];
    if (!fieldSettings) return orderedFields;

    return orderedFields.filter(fieldName => {
      if (fieldName.startsWith("s_i_")) return true;
      if (fieldName.startsWith("i_i_") || fieldName === "line_item") return true;
      return isFieldSettingEnabled(fieldSettings, fieldName);
    });
  }, []);

  useEffect(() => {
    if (loading || initialFormValues) return;

    setInitialFormValues({
      keepOpenOnNextSubmit: false,
      fieldSettings: fieldSettings || {},
      receivingSettings: receivingSettings || {},
      shipmentPdfFields: project.shipmentPdfFields,
      shipmentItemPdfFields: project.shipmentItemPdfFields,
      inventoryRequestPdf: project.inventoryRequestPdf,
      shipmentItemMoveLabelPdfFields: project.shipmentItemMoveLabelPdfFields,
      storeCardFields: project.storeCardFields,
    });
    setSavedInventorySettings({
      fieldSettings: fieldSettings || {},
      receivingSettings: receivingSettings || {},
    });
  }, [
    fieldSettings,
    initialFormValues,
    loading,
    project.inventoryRequestPdf,
    project.shipmentItemMoveLabelPdfFields,
    project.shipmentItemPdfFields,
    project.shipmentPdfFields,
    project.storeCardFields,
    receivingSettings,
  ]);

  const saveProjectSettings = useCallback(async (values, options = {}) => {
    const {
      closeOnSuccess = true,
      notifyOnSuccess = true,
      markInventorySettingsSaved = false,
    } = options;
    const {
      keepOpenOnNextSubmit,
      fieldSettings,
      receivingSettings,
      shipmentPdfFields,
      shipmentItemPdfFields,
      inventoryRequestPdf,
      shipmentItemMoveLabelPdfFields,
      storeCardFields,
    } = values;

    return updateProjectSettings({
      variables: {
        projectId,
        fieldSettings,
        receivingSettings,
        shipmentPdfFields: shipmentPdfFields ? {
          ...shipmentPdfFields,
          ordered_fields: cleanPdfFields(shipmentPdfFields.ordered_fields, fieldSettings),
        } : null,
        shipmentItemPdfFields: shipmentItemPdfFields ? {
          ...shipmentItemPdfFields,
          ordered_fields: cleanPdfFields(shipmentItemPdfFields.ordered_fields, fieldSettings),
        } : null,
        inventoryRequestPdf: inventoryRequestPdf ? {
          ...inventoryRequestPdf,
          ordered_fields: cleanPdfFields(inventoryRequestPdf.ordered_fields, fieldSettings),
        } : null,
        shipmentItemMoveLabelPdfFields: shipmentItemMoveLabelPdfFields ? {
          ...shipmentItemMoveLabelPdfFields,
          ordered_fields: cleanPdfFields(
            shipmentItemMoveLabelPdfFields.ordered_fields,
            fieldSettings
          ),
        } : null,
        storeCardFields,
      },
      refetchQueries: [
        {
          query: FETCH_PROJECT_QUERY,
          variables: { id: projectId },
        },
        {
          query: CUSTOM_FIELD_TEMPLATES_QUERY,
          variables: {
            projectId,
            sortBy: "default_label",
            sortDirection: "asc",
          },
        },
      ],
      awaitRefetchQueries: true,
      onSuccess: () => {
        if (markInventorySettingsSaved) {
          setSavedInventorySettings({
            fieldSettings,
            receivingSettings,
          });
        }

        if (notifyOnSuccess) {
          notifySuccess("Settings has been updated");
        }

        if (closeOnSuccess && !keepOpenOnNextSubmit && onClose) {
          onClose();
        }
      },
      onFailure: errors => {
        notifyError(errors);
      },
    });
  }, [cleanPdfFields, notifyError, notifySuccess, onClose, projectId, updateProjectSettings]);

  const handleUpdateProjectSettings = async ({
    keepOpenOnNextSubmit,
    fieldSettings,
    receivingSettings,
    shipmentPdfFields,
    shipmentItemPdfFields,
    inventoryRequestPdf,
    shipmentItemMoveLabelPdfFields,
    storeCardFields,
  }) => {
    return saveProjectSettings({
      keepOpenOnNextSubmit,
      fieldSettings,
      receivingSettings,
      shipmentPdfFields,
      shipmentItemPdfFields,
      inventoryRequestPdf,
      shipmentItemMoveLabelPdfFields,
      storeCardFields,
    }, {
      closeOnSuccess: true,
      notifyOnSuccess: true,
      markInventorySettingsSaved: true,
    });
  };

  if (loading || !initialFormValues || !savedInventorySettings) {
    return null;
  }

  return (
    <Formik initialValues={initialFormValues} onSubmit={handleUpdateProjectSettings}>
      {formik => {
        if (formikRef) {
          formikRef.current = {
            ...formikRef.current,
            ...formik,
            autosaveInventorySettings: () =>
              saveProjectSettings(formik.values, {
                closeOnSuccess: false,
                notifyOnSuccess: false,
                markInventorySettingsSaved: true,
              }),
          };
        }

        return (
          <Form>
            <ConfigurationFormBridge
              formikRef={formikRef}
              initialFormValues={initialFormValues}
              savedInventorySettings={savedInventorySettings}
            />
            {children}
          </Form>
        );
      }}
    </Formik>
  );
}
